import Alert from "@components/Alert";
import { useBoat } from "@state/DataStore";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect } from "react";
import Button from "@components/Button";
import Input from "@components/Input";
import useSettingsStore from "@state/SettingsStore";
import useMessageStore from "@state/MessageStore";
import useAjax from "@hooks/use-ajax.hook";
import Switch from "@components/Switch";
import Label from "@components/Label";
import useLoading from "src/services/LoadingService";
import { useTranslation } from "react-i18next";
import useAddSettings from "./use-add-settings";

const Settings = () => {
  const { date, boatId } = useParams();
  const { t } = useTranslation();

  const navigate = useNavigate();
  if (boatId == null || date == null) {
    throw new Error(t<string>("No active term"));
  }
  const boat = useBoat(parseInt(boatId, 10));

  if (boat == null) {
    throw new Error(t<string>("Boat is not found"));
  }

  const settingsState = useSettingsStore((state) => state);

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "price" || name === "sale") {
      useSettingsStore.getState().setField(name, parseInt(value, 10));
    }
  };

  const onDisabledChange = () => {
    useSettingsStore
      .getState()
      .setField("disabled", !useSettingsStore.getState().disabled);
  };

  const { loading, error, addSettings } = useAddSettings();
  const { request, error: ajaxError, loading: ajaxLoading } = useAjax();

  useLoading(ajaxLoading);

  const loadSetting = useCallback(async () => {
    const result = await request<{
      price: number | undefined;
      sale: number | undefined;
      disabled: boolean | undefined;
    }>("get_setting", { date: dayjs(date).format("YYYY-MM-DD"), boatId });
    if (result) {
      useSettingsStore.getState().setField("price", result.price);
      useSettingsStore.getState().setField("sale", result.sale);
      useSettingsStore.getState().setField("disabled", result.disabled);
    }
  }, [request, date, boatId]);

  useEffect(() => {
    loadSetting();
  }, [loadSetting]);

  const onBack = useCallback(() => {
    navigate("/");
    useSettingsStore.getState().reset();
  }, [navigate]);

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const { price, sale, disabled } = useSettingsStore.getState();
      const id = await addSettings({
        price: price ?? boat.price,
        sale,
        disabled,
        boatId: parseInt(boatId, 10),
        date: new Date(date),
      });
      if (id !== null) {
        useMessageStore.getState().addMessage(
          t("settingsSaved", {
            date: new Date(date).toLocaleDateString(),
            boat: boat.name,
          })
        );
        onBack();
        useSettingsStore.getState().reset();
      }
    },
    [addSettings, boat.name, boat.price, boatId, date, onBack, t]
  );

  return (
    <>
      <h1 className="text-5xl mb-4">{t("Setting the date")}</h1>
      <h2 className="text-4xl font-extrabold mb-4">
        {boat.name ?? t("Unknown boat")}
        <small className="block font-semibold text-gray-500 dark:text-gray-400">
          {new Date(date).toLocaleDateString()} -{" "}
          {dayjs(new Date(date)).add(7, "day").toDate().toLocaleDateString()}
        </small>
      </h2>
      {error && <Alert type="error">{error}</Alert>}
      {ajaxError && <Alert type="error">{ajaxError}</Alert>}
      <form onSubmit={onSubmit}>
        <Input
          name="price"
          label={t("Price")}
          value={(settingsState.price ?? boat.price).toString()}
          onChange={onChange}
          required
          type="number"
        />
        <Input
          name="sale"
          label={t("Price after discount")}
          value={settingsState.sale?.toString() ?? ""}
          onChange={onChange}
          type="number"
        />
        <div className="mb-6">
          <Label name="disabled">{t("Disable this term")}</Label>
          <Switch onChange={onDisabledChange} value={settingsState.disabled} />
        </div>
        <div className="flex flex-row gap-4 justify-end">
          <Button importance="secondary" onClick={onBack}>
            {t("Back")}
          </Button>
          <Button type="submit" loading={loading} disabled={loading}>
            {t("Save")}
          </Button>
        </div>
      </form>
    </>
  );
};

export default Settings;
